import { useEffect, useState } from "react";

/**
 * SPA 内クライアントルーター。
 *
 * Cloudflare Workers + Static Assets binding の `not_found_handling:
 * "single-page-application"` 設定により、`/about` などの未知パスは
 * すべて `index.html` にフォールバックされる。クライアント側で
 * `window.location.pathname` を見て表示するページを切り替える。
 *
 * 軽量にするため react-router 等は使わず、`history.pushState` +
 * `popstate` だけで実装する。`navigate` は popstate を手動 dispatch して
 * `useCurrentPath` を再描画させる。
 *
 * AdSense / SEO 観点: 静的なパス (/about, /privacy, /terms, /disclaimer)
 * を sitemap.xml に列挙し、クライアントが対応ページを描画することで
 * Googlebot は JS 実行後にコンテンツをインデックス可能。
 */

export const ROUTES = {
  home: "/",
  about: "/about",
  privacy: "/privacy",
  terms: "/terms",
  disclaimer: "/disclaimer",
} as const;

export type RoutePath = (typeof ROUTES)[keyof typeof ROUTES];

export function useCurrentPath(): string {
  const [path, setPath] = useState<string>(() =>
    typeof window === "undefined" ? "/" : window.location.pathname,
  );

  useEffect(() => {
    const handler = () => setPath(window.location.pathname);
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, []);

  return path;
}

/**
 * 任意のパスに遷移する。同一パスへの遷移は no-op。
 * pushState 自体は popstate を発火しないので、手動で dispatch して
 * useCurrentPath の再描画をトリガする。
 */
export function navigate(to: string) {
  if (typeof window === "undefined") return;
  if (window.location.pathname === to) return;
  window.history.pushState(null, "", to);
  window.dispatchEvent(new PopStateEvent("popstate"));
  // 新しいページの先頭にスクロール (静的ページは記事スタイルなので必要)
  window.scrollTo(0, 0);
}

/**
 * 静的ページ用に <title> と <meta name="description"> を動的更新する hook。
 * Googlebot は JS 実行できるので、ルート遷移時に書き換えるだけで OK。
 *
 * canonical も併せて差し替えると更に良いが、本サイトは単一ドメインなので
 * 各ページの canonical は <link rel="canonical"> を window.location.href
 * に揃える方針で十分。
 */
export function useDocumentMeta(opts: {
  title: string;
  description: string;
  /** ページ固有の canonical パス。指定なければ現在の pathname。 */
  canonicalPath?: string;
}) {
  const { title, description, canonicalPath } = opts;
  useEffect(() => {
    document.title = title;

    setMetaContent("description", description);
    setMetaProperty("og:title", title);
    setMetaProperty("og:description", description);
    setMetaContent("twitter:title", title);
    setMetaContent("twitter:description", description);

    const path = canonicalPath ?? window.location.pathname;
    const origin = window.location.origin;
    setLinkHref("canonical", `${origin}${path}`);
    setMetaProperty("og:url", `${origin}${path}`);
  }, [title, description, canonicalPath]);
}

function setMetaContent(name: string, content: string) {
  let el = document.querySelector(
    `meta[name="${name}"]`,
  ) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setMetaProperty(property: string, content: string) {
  let el = document.querySelector(
    `meta[property="${property}"]`,
  ) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLinkHref(rel: string, href: string) {
  let el = document.querySelector(
    `link[rel="${rel}"]`,
  ) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}
